import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { patientRegistrationApprovalService } from '../services/patientRegistrationApprovalService';
import type {
  PatientRegistrationApprovalActionRequest,
  PatientRegistrationApprovalListQuery,
} from '../types/patientRegistrationApproval';

const keys = {
  list: (programId: string, query: PatientRegistrationApprovalListQuery) =>
    ['patient-registration-approvals', programId, query] as const,
  detail: (programId: string, approvalId: string | null) =>
    ['patient-registration-approval', programId, approvalId] as const,
  pendingCount: (programId: string) =>
    ['patient-registration-approvals-pending-count', programId] as const,
};

export function usePatientRegistrationApprovals(
  programId: string,
  query: PatientRegistrationApprovalListQuery,
) {
  return useQuery({
    queryKey: keys.list(programId, query),
    queryFn: () => patientRegistrationApprovalService.getApprovals(programId, query).then(r => r.data),
    enabled: Boolean(programId),
    staleTime: 15_000,
  });
}

export function usePatientRegistrationApprovalDetail(programId: string, approvalId: string | null) {
  return useQuery({
    queryKey: keys.detail(programId, approvalId),
    queryFn: () => patientRegistrationApprovalService.getApproval(programId, approvalId!).then(r => r.data),
    enabled: Boolean(programId && approvalId),
    staleTime: 15_000,
  });
}

export function usePatientRegistrationApprovalPendingCount(programId: string) {
  return useQuery({
    queryKey: keys.pendingCount(programId),
    queryFn: () => patientRegistrationApprovalService.getPendingCount(programId).then(r => r.data.pendingCount),
    enabled: Boolean(programId),
    staleTime: 15_000,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, programId: string) {
  void qc.invalidateQueries({ queryKey: ['patient-registration-approvals', programId] });
  void qc.invalidateQueries({ queryKey: ['patient-registration-approval', programId] });
  void qc.invalidateQueries({ queryKey: ['patient-registration-approvals-pending-count', programId] });
  void qc.invalidateQueries({ queryKey: ['hcpDashboardSummary', programId] });
}

export function useApprovePatientRegistrationApproval(programId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ approvalId, request }: { approvalId: string; request: PatientRegistrationApprovalActionRequest }) =>
      patientRegistrationApprovalService.approve(programId, approvalId, request),
    onSuccess: () => invalidateAll(qc, programId),
  });
}

export function useRejectPatientRegistrationApproval(programId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ approvalId, request }: { approvalId: string; request: PatientRegistrationApprovalActionRequest }) =>
      patientRegistrationApprovalService.reject(programId, approvalId, request),
    onSuccess: () => invalidateAll(qc, programId),
  });
}
